import React, { useState } from "react";
import { Mail, Send, Instagram, Music2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { profile } from "../mock";
import { Reveal } from "../hooks/useReveal";

const socialIcon = { Instagram: Instagram, TikTok: Music2, Email: Mail };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé — merci ! Je vous réponds très vite. ✨");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        toast.error("Veuillez vérifier votre adresse email.");
      } else {
        toast.error("Oups, l'envoi a échoué. Réessayez dans un instant.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#3A302A] text-[#FAF5EF] overflow-hidden">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#B0603F]/25 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
        {/* left */}
        <Reveal>
          <span className="font-body text-xs tracking-luxe uppercase text-[#C88A63]">Contact</span>
          <h2 className="font-serif-display text-5xl md:text-7xl font-light mt-3 leading-[0.95]">
            Travaillons
            <span className="block font-script text-[#C88A63] text-6xl md:text-8xl">ensemble</span>
          </h2>
          <p className="mt-6 max-w-md font-body text-[#E7D8C6] leading-relaxed text-lg">
            Envie d'une collaboration sur-mesure, reflétant votre marque et séduisant votre public ?
            Écrivez-moi, créons quelque chose de beau.
          </p>

          <a
            href={`mailto:${profile.email}`}
            className="group inline-flex items-center gap-3 mt-8 font-serif-display text-2xl md:text-3xl text-[#FAF5EF] hover:text-[#C88A63] transition-colors"
          >
            <Mail size={24} />
            {profile.email}
          </a>

          <div className="mt-10 flex flex-wrap gap-4">
            {profile.socials.map((s) => {
              const Icon = socialIcon[s.label] || ArrowUpRight;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  className="group inline-flex items-center gap-3 rounded-full border border-[#B79A80]/40 px-5 py-3 hover:border-[#C88A63] hover:bg-[#FAF5EF]/5 transition-all"
                >
                  <Icon size={18} className="text-[#C88A63]" />
                  <span className="font-body text-sm">{s.handle}</span>
                </a>
              );
            })}
          </div>
        </Reveal>

        {/* form */}
        <Reveal>
          <form
            onSubmit={submit}
            className="bg-[#FAF5EF] text-[#3A302A] rounded-3xl p-8 md:p-10 shadow-2xl"
          >
            <h3 className="font-serif-display text-3xl mb-6">Parlez-moi de votre projet</h3>
            <div className="space-y-5">
              <div>
                <label className="font-body text-xs tracking-wide-2 uppercase text-[#6B5D53]">Nom</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-[#E7D8C6] focus:border-[#B0603F] outline-none py-2.5 font-body text-[#3A302A] transition-colors"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label className="font-body text-xs tracking-wide-2 uppercase text-[#6B5D53]">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-[#E7D8C6] focus:border-[#B0603F] outline-none py-2.5 font-body text-[#3A302A] transition-colors"
                  placeholder="vous@email.com"
                />
              </div>
              <div>
                <label className="font-body text-xs tracking-wide-2 uppercase text-[#6B5D53]">Message</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-[#E7D8C6] focus:border-[#B0603F] outline-none py-2.5 font-body text-[#3A302A] transition-colors resize-none"
                  placeholder="Décrivez votre projet..."
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="group mt-8 w-full inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#3A302A] text-[#FAF5EF] text-sm tracking-wide-2 uppercase hover:bg-[#B0603F] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Envoi en cours..." : "Envoyer le message"}
              <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
